import { useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { format, parseISO } from "date-fns";

import { useClaims } from "@/hooks/use-claims";
import { useNavigationType } from "react-router";

import { useSearchParamWriter } from "@/hooks/use-search-param-writer";
import { useResolvedConfig, type EntityType } from "@/hooks/use-resolved-config";
import {
  shiftDay,
  weekStartDay,
  zonedDay,
  zonedDayEnd,
  zonedDayStart,
  type DayRange,
} from "@/lib/zoned-day";
import { useAdminOrgStore } from "@/stores/admin-org";
import { useWasteFiltersStore, type SharedWasteFilters } from "@/stores/waste-filters";

export type DatePreset =
  | "today"
  | "7d"
  | "30d"
  | "90d"
  | "this_week"
  | "last_week"
  | "custom"
  | "all";

export type WasteFilters = {
  preset: DatePreset;
  date_from: string | null;
  date_to: string | null;
  site_id: string | null;
  kitchen_id: string | null;
  device_serial: string | null;
  waste_type: string | null;
  classification_level: number | null;
  status: string | null;
  service_time: string | null;
  // Shared waste filter-popover fields (POS2-753). Time-of-day window, price/weight
  // ranges, and the identified-food filter.
  time_from: string | null;
  time_to: string | null;
  price_min: number;
  price_max: number;
  weight_min: number;
  weight_max: number;
  food: string | null;
  // Confirmation state (POS2-787): null = all, true = confirmed, false = unconfirmed.
  is_confirmed: boolean | null;
  sort_by: "logged_at" | "item_weight" | "cost_estimate" | "classification_level";
  sort_dir: "asc" | "desc";
  page: number;
  page_size: number;
};

/** The fields the FilterPopover edits as a draft and commits on "Apply". */
export type WasteFilterDraft = Pick<
  WasteFilters,
  | "date_from"
  | "date_to"
  | "time_from"
  | "time_to"
  | "price_min"
  | "price_max"
  | "weight_min"
  | "weight_max"
  | "site_id"
  | "kitchen_id"
  | "waste_type"
  | "device_serial"
  | "food"
  | "service_time"
  | "classification_level"
  | "is_confirmed"
>;

export function toFilterDraft(f: WasteFilters): WasteFilterDraft {
  return {
    date_from: f.date_from,
    date_to: f.date_to,
    time_from: f.time_from,
    time_to: f.time_to,
    price_min: f.price_min,
    price_max: f.price_max,
    weight_min: f.weight_min,
    weight_max: f.weight_max,
    site_id: f.site_id,
    kitchen_id: f.kitchen_id,
    waste_type: f.waste_type,
    device_serial: f.device_serial,
    food: f.food,
    service_time: f.service_time,
    classification_level: f.classification_level,
    is_confirmed: f.is_confirmed,
  };
}

/** Range-slider bounds; also the "no filter" default at each end. */
export const PRICE_MAX = 10000;
export const WEIGHT_MAX = 1000;

const DEFAULTS: WasteFilters = {
  preset: "7d",
  date_from: null,
  date_to: null,
  site_id: null,
  kitchen_id: null,
  device_serial: null,
  waste_type: null,
  classification_level: null,
  status: null,
  service_time: null,
  time_from: null,
  time_to: null,
  price_min: 0,
  price_max: PRICE_MAX,
  weight_min: 0,
  weight_max: WEIGHT_MAX,
  food: null,
  // Null = all events (confirmed + unconfirmed).
  is_confirmed: null,
  // "Recommended" default (POS2-759): highest-confidence classifications first
  // (L4 → L1), i.e. the events most worth confirming/correcting.
  sort_by: "classification_level",
  sort_dir: "desc",
  page: 0,
  // Multiples of 3 to line up with the card grid (POS2-759).
  page_size: 12,
};

const STRING_KEYS = [
  "site_id",
  "kitchen_id",
  "device_serial",
  "waste_type",
  "status",
  "service_time",
  "food",
] as const;

/**
 * URL-backed waste filter state shared by Dashboard and Transactions.
 * `defaultPreset` sets the page's date window when the URL carries none — the
 * homepage passes "this_week" (POS2-865); every instance rendered on the same
 * page must pass the same value or their computed ranges diverge.
 * `defaultSiteId` marks a single-site page (the Dashboard): when no site is
 * persisted or in the URL, the seed falls back to it (POS2-852), and it is the
 * site the window's timezone resolves against (POS2-1039). It carries the same
 * "every instance on the page must pass the same value" rule as `defaultPreset`.
 * Omit it on multi-site pages; pass `null` while the default is still loading.
 */
export function useWasteFilters(
  defaultPreset: DatePreset = DEFAULTS.preset,
  defaultSiteId?: string | null,
) {
  const [searchParams] = useSearchParams();
  // Every write bases itself on the last write rather than on what the last render saw, so a
  // write landing before the previous one commits can't drop its params (POS2-1048).
  const { write, reset } = useSearchParamWriter({ replace: true });
  // PUSH means a link or a nav item brought us here; every write this hook makes is a REPLACE.
  // That is what separates a wiped URL from a site the person deliberately cleared (POS2-1279).
  const navigationType = useNavigationType();
  const seeded = useRef(false);

  const filters: WasteFilters = useMemo(() => {
    const preset = (searchParams.get("preset") as DatePreset) ?? defaultPreset;
    return {
      preset,
      date_from: searchParams.get("date_from"),
      date_to: searchParams.get("date_to"),
      site_id: searchParams.get("site_id"),
      kitchen_id: searchParams.get("kitchen_id"),
      device_serial: searchParams.get("device_serial"),
      waste_type: searchParams.get("waste_type"),
      classification_level: parseLevel(searchParams.get("tier")),
      status: searchParams.get("status"),
      service_time: searchParams.get("service_time"),
      time_from: searchParams.get("time_from"),
      time_to: searchParams.get("time_to"),
      price_min: parseNum(searchParams.get("price_min"), 0, 0, PRICE_MAX),
      price_max: parseNum(searchParams.get("price_max"), PRICE_MAX, 0, PRICE_MAX),
      weight_min: parseNum(searchParams.get("weight_min"), 0, 0, WEIGHT_MAX),
      weight_max: parseNum(searchParams.get("weight_max"), WEIGHT_MAX, 0, WEIGHT_MAX),
      food: searchParams.get("food"),
      is_confirmed: parseTriBool(searchParams.get("confirmed")),
      sort_by: (searchParams.get("sort_by") as WasteFilters["sort_by"]) ?? DEFAULTS.sort_by,
      sort_dir: (searchParams.get("sort_dir") as WasteFilters["sort_dir"]) ?? DEFAULTS.sort_dir,
      page: Math.max(0, Number.parseInt(searchParams.get("page") ?? "0", 10) || 0),
      page_size: parsePageSize(searchParams.get("page_size")),
    };
  }, [searchParams, defaultPreset]);

  const setFilter = useCallback(
    <K extends keyof WasteFilters>(key: K, value: WasteFilters[K] | null) => {
      write((next) => {
        // Any filter change other than page itself resets pagination.
        if (key !== "page") next.delete("page");
        writeParam(next, key, value);
      });
    },
    [write],
  );

  const setPreset = useCallback(
    (preset: DatePreset) => {
      write((next) => {
        next.delete("page");
        next.delete("date_from");
        next.delete("date_to");
        if (preset === defaultPreset) {
          next.delete("preset");
        } else {
          next.set("preset", preset);
        }
      });
    },
    [write, defaultPreset],
  );

  const setSort = useCallback(
    (sortBy: WasteFilters["sort_by"], sortDir: WasteFilters["sort_dir"]) => {
      // Both params in a single update — still the right shape (one logical change, one
      // history entry), though no longer the thing standing between us and a lost param.
      write((next) => {
        next.delete("page");
        writeParam(next, "sort_by", sortBy);
        writeParam(next, "sort_dir", sortDir);
      });
    },
    [write],
  );

  const setCustomRange = useCallback(
    (from: string | null, to: string | null) => {
      write((next) => {
        next.delete("page");
        next.set("preset", "custom");
        if (from) next.set("date_from", from);
        else next.delete("date_from");
        if (to) next.set("date_to", to);
        else next.delete("date_to");
      });
    },
    [write],
  );

  const setRange = useCallback(
    (
      minKey: "price_min" | "weight_min",
      maxKey: "price_max" | "weight_max",
      min: number,
      max: number,
    ) => {
      // Both bounds in one update, as with setSort — one logical change, one history entry.
      write((next) => {
        next.delete("page");
        writeParam(next, minKey, min);
        writeParam(next, maxKey, max);
      });
    },
    [write],
  );

  const applyFilters = useCallback(
    // `extraParams` lets a caller commit non-WasteFilter URL params in the SAME
    // update (Explore's `split`), so the popover's Apply is one atomic change.
    // null/"" value deletes the param.
    (draft: WasteFilterDraft, extraParams?: Record<string, string | null>) => {
      // Commit the whole popover draft in ONE update. Non-popover params (sort, status)
      // are preserved by starting from the current params.
      write((next) => {
        next.delete("page");
        // Date pickers map to a custom range; cleared dates mean "all time"
        // (the popover seeds the pickers with the active window, so empty is
        // an explicit choice, not the default).
        if (draft.date_from || draft.date_to) {
          next.set("preset", "custom");
          if (draft.date_from) next.set("date_from", draft.date_from);
          else next.delete("date_from");
          if (draft.date_to) next.set("date_to", draft.date_to);
          else next.delete("date_to");
        } else {
          // Empty dates mean THIS page's default window, not all time (POS2-1049) — which is what
          // the pickers' placeholders promise. Deleting the param lets the page default apply:
          // Dashboard this week, Transactions last 7 days, Explore its granularity default (whose
          // own default happens to be "all", so its behaviour is unchanged).
          //
          // Consequence, decided with Artem: all-time is no longer reachable by emptying the
          // fields. Typing an early start date still gets there.
          next.delete("preset");
          next.delete("date_from");
          next.delete("date_to");
        }
        writeParam(next, "time_from", draft.time_from);
        writeParam(next, "time_to", draft.time_to);
        writeParam(next, "price_min", draft.price_min);
        writeParam(next, "price_max", draft.price_max);
        writeParam(next, "weight_min", draft.weight_min);
        writeParam(next, "weight_max", draft.weight_max);
        writeParam(next, "site_id", draft.site_id);
        writeParam(next, "kitchen_id", draft.kitchen_id);
        writeParam(next, "waste_type", draft.waste_type);
        writeParam(next, "device_serial", draft.device_serial);
        writeParam(next, "food", draft.food);
        writeParam(next, "service_time", draft.service_time);
        writeParam(next, "classification_level", draft.classification_level);
        writeParam(next, "is_confirmed", draft.is_confirmed);
        if (extraParams) {
          for (const [key, value] of Object.entries(extraParams)) {
            if (value == null || value === "") next.delete(key);
            else next.set(key, value);
          }
        }
      });
    },
    [write],
  );

  const clear = useCallback(() => {
    // Reset clears the persisted subset too (POS2-852), so it doesn't reappear on the
    // next page. Unlatch the seed so a single-site page re-applies its default site
    // afterwards (an empty store means the seed writes only the default, not stale filters).
    useWasteFiltersStore.getState().clearShared();
    seeded.current = false;
    reset();
  }, [reset]);

  // Platform-admin org switch, and org/site switcher SITE change (POS2-851): filter state is
  // URL-based and outlives the switcher context, so a stale site_id must be dropped and the new
  // default reseeded. Handled at the top of the seed effect below (POS2-984) — not a separate
  // effect — so it can bail before reading the stale URL.
  const orgId = useAdminOrgStore((s) => s.orgId);
  // The switcher's selected site seeds site_id on every page (POS2-851); "" = org level.
  const switcherSiteId = useAdminOrgStore((s) => s.siteId);
  const prevOrgId = useRef(orgId);
  const prevSiteId = useRef(switcherSiteId);

  // The window's timezone, resolved on the same kitchen → site → org entity the pages build
  // for currency (POS2-1039). Resolved HERE rather than taken as an argument: several
  // instances of this hook are mounted per page (the page itself, FilterPopover,
  // PeriodControl) and they must all land on the same window, so the entity comes from the
  // shared URL params rather than from whatever each caller happened to be handed.
  //
  // The one input that is NOT shared is `defaultSiteId`, so a single-site page must pass it
  // to EVERY instance (as it already must for `defaultPreset`): the URL only carries
  // `site_id` once the seed writes it, and a deep link that already carries a shared param
  // latches the seed before that ever happens. An instance left without it would then
  // resolve the org's zone while the page resolves the site's.
  //
  // The chain's UTC tail applies while the config is in flight.
  const { orgId: claimsOrgId, isPlatformAdmin } = useClaims();
  const configSiteId = filters.site_id ?? defaultSiteId ?? null;
  const configEntity: { type: EntityType; id: string | null } = filters.kitchen_id
    ? { type: "kitchen", id: filters.kitchen_id }
    : configSiteId
      ? { type: "site", id: configSiteId }
      : { type: "organisation", id: (isPlatformAdmin ? orgId : claimsOrgId) || null };
  const resolvedTimezone = useResolvedConfig(configEntity.type, configEntity.id).data?.config
    ?.timezone?.value;
  const timezone = typeof resolvedTimezone === "string" ? resolvedTimezone : "UTC";

  const hasActiveFilters = useMemo(
    () =>
      filters.preset !== defaultPreset ||
      STRING_KEYS.some((k) => filters[k] != null) ||
      filters.classification_level != null ||
      filters.time_from != null ||
      filters.time_to != null ||
      filters.price_min !== DEFAULTS.price_min ||
      filters.price_max !== DEFAULTS.price_max ||
      filters.weight_min !== DEFAULTS.weight_min ||
      filters.weight_max !== DEFAULTS.weight_max ||
      filters.is_confirmed != null,
    [filters, defaultPreset],
  );

  /** The window as inclusive civil days in the site's zone — what a date column, a picker
   * or a label wants. Recovering one of these from `dateRange` in the viewer's zone is the
   * bug this ticket exists to remove, so consumers take these strings directly. */
  const dayRange = useMemo(
    () => computeDayRange(filters, zonedDay(new Date(), timezone)),
    [filters, timezone],
  );

  /** This page's *default* window as inclusive civil days — what the date pickers show as their
   * placeholder, so an empty field reads as "the default" rather than "unbounded" (POS2-1049).
   * Derived here rather than at each call site so the three waste pages cannot disagree about
   * what their own default is. An open-ended preset (7d/30d/90d) has no `to`, so it closes at
   * today: a placeholder has to render something. */
  const defaultRange = useMemo(() => {
    const today = zonedDay(new Date(), timezone);
    const span = computeDayRange({ ...DEFAULTS, preset: defaultPreset }, today);
    return { from: span.from ?? today, to: span.to ?? today };
  }, [defaultPreset, timezone]);

  /** The same window as instants at site-tz midnight — what the stats/events APIs take. */
  const dateRange = useMemo(
    () => ({
      from: dayRange.from ? zonedDayStart(dayRange.from, timezone).toISOString() : null,
      to: dayRange.to ? zonedDayEnd(dayRange.to, timezone).toISOString() : null,
    }),
    [dayRange, timezone],
  );

  // Seed the filters once, on mount, when the URL carries no shared-filter param
  // (POS2-852): restore the subset last committed on another waste page — arriving via
  // the sidebar, or a notification deep-link that carries only commentEvent. For a
  // single-site page (`defaultSiteId` given) fall back to that site when none is
  // persisted. This is the SOLE mount-time writer of the shared params: splitting it
  // (hook seeds + the Dashboard writing its default site separately) raced on
  // react-router's params and clobbered the restore. Written into the URL so the
  // several hook instances per page keep coordinating through it; explicit URL params
  // win; commentEvent/comment are preserved.
  //
  // Latched with a ref so clearing the last filter to an empty URL does NOT re-seed the
  // still-populated store. `clear()` unlatches it, so Reset / org switch re-apply the
  // default (against a now-empty store).
  useEffect(() => {
    // Org switched while this hook is mounted (platform-admin picker). Drop the previous
    // org's persisted + URL filters and re-arm the seed, then BAIL this pass: `searchParams`
    // still holds the old org's site_id here, so reading it now would latch the seed
    // (urlHasFilters=true) without ever reseeding. Emptying the URL re-runs this effect
    // clean, where the block below seeds the new org's default site (POS2-984).
    if (prevOrgId.current !== orgId || prevSiteId.current !== switcherSiteId) {
      prevOrgId.current = orgId;
      prevSiteId.current = switcherSiteId;
      seeded.current = false;
      useWasteFiltersStore.getState().clearShared();
      reset();
      return;
    }
    const wantsDefaultSite = defaultSiteId !== undefined;
    // The URL has lost its `site_id` (POS2-1279). Every sidebar item is a static path —
    // `to="/"`, `to="/waste/transactions"` — which react-router reads as that pathname with an
    // EMPTY search string, and the routes carry no `key`, so the page never unmounts. A second
    // click therefore changed only `location.search` and the latch refused to re-seed: the
    // dashboard fell through to the alphabetically first site, and Transactions went org-wide,
    // both while the switcher still named the site that had been picked.
    //
    // **Two page kinds, two rules, because they have different legitimate states.** A single-site
    // page has no site-less state at all, so it re-seeds whenever the URL carries none. A
    // multi-site page does — "all sites" is a real choice on Transactions and Explore — so it
    // re-seeds only when a *navigation* brought us here. `useWasteFilters` writes with
    // `replace: true`, so every filter write is a REPLACE and a nav click is a PUSH: without that
    // gate, picking "All sites" while the switcher named one would have the site put straight
    // back, which is the user's own choice being overruled.
    //
    // Narrower than re-seeding whenever the search is empty in a third way too: the latch exists
    // so that clearing the last filter by hand does not restore the still-populated store, and
    // that stays true — `restoreShared` is false on a re-seed, so the site is all that returns.
    const lostSite =
      !searchParams.has("site_id") &&
      (wantsDefaultSite || (!!switcherSiteId && navigationType === "PUSH"));
    if (seeded.current && !lostSite) return;
    const reseedingSite = seeded.current;
    const urlHasFilters = SHARED_PARAM_KEYS.some((k) => searchParams.has(k));
    // Ignore persisted filters from a different org (POS2-967): site_id is org-specific, so
    // a platform admin who switched org on a page with no mounted useWasteFilters hook (e.g.
    // DataBridge, where the branch above never runs) must not inherit the prior org's
    // site_id — that 404s config/resolved and returns no data until a manual re-pick.
    const store = useWasteFiltersStore.getState();
    const shared = store.orgId === orgId ? store.shared : null;
    // Precedence: the switcher's site was picked deliberately and on THIS org, so it outranks
    // a site persisted while browsing earlier; the persisted one still outranks the page's
    // default (POS2-852). Org level ("") falls through to the page default (single-site pages)
    // or null → all sites (POS2-851). The persisted site used to win outright, so a site picked
    // on a page with no hook mounted — Accounts, then Dashboard — was silently ignored
    // (POS2-1068).
    const site = switcherSiteId || shared?.site_id || defaultSiteId || null;
    // A single-site page must wait for its default site to load before latching, else it
    // would seed a site-less (org-wide) URL and never retry. This holds whatever the URL
    // carries: `preset` counts towards urlHasFilters, so a URL with one used to latch the
    // seed while the /sites query was still in flight, leaving the page org-wide until it
    // was remounted (POS2-1068).
    if (wantsDefaultSite && !site) return;
    seeded.current = true;
    // Explicit URL params beat the store (POS2-941), so a URL already carrying shared filters
    // keeps them — but a single-site page still needs its site written, which is the half that
    // `urlHasFilters` used to skip.
    const restoreShared = !reseedingSite && !urlHasFilters && !!shared;
    // A single-site page seeds its site even when the URL already carries shared params, since
    // `preset` alone trips urlHasFilters; a multi-site page keeps deferring to the URL.
    const needsSite =
      !!site && !searchParams.has("site_id") && (!urlHasFilters || wantsDefaultSite);
    if (!restoreShared && !needsSite) return;
    // This write races user clicks by construction — it fires from an effect gated on the sites
    // query, so it can land mid-interaction. Going through `write` is what stops it dropping a
    // control the user just set, or having its own `site_id` dropped and never retried (the
    // `seeded` latch above means a lost seed is not re-attempted) — POS2-1048.
    write((next) => {
      if (restoreShared && shared) writeSharedToParams(next, shared);
      // Guarded on the URL as it arrived, not on `next`: restoring the shared subset writes the
      // persisted site_id, and testing `next` let that beat a site the switcher had picked
      // (POS2-1068). `site` already encodes the precedence, so it settles the question.
      if (site && !searchParams.has("site_id")) next.set("site_id", site);
    });
  }, [searchParams, write, reset, defaultSiteId, orgId, switcherSiteId, navigationType]);

  // Mirror committed filters into the store so the next waste page can restore them.
  // Only when active: writing defaults would clobber a still-valid persisted set on a
  // fresh page load before the seed runs. Reset/org-switch clear the store via clear().
  useEffect(() => {
    // Tag the persisted filters with the current org so the seed can reject them under a
    // different org (POS2-967). orgId is read at persist time (getState, not a dep) — as a
    // dep it would re-run on an org switch and re-persist the outgoing org's filters right
    // after the reset effect above cleared them.
    if (hasActiveFilters) {
      useWasteFiltersStore
        .getState()
        .setShared(pickShared(filters), useAdminOrgStore.getState().orgId);
    }
  }, [filters, hasActiveFilters]);

  return {
    filters,
    dateRange,
    dayRange,
    defaultRange,
    /** The site this page is scoped to rather than filtered by — the switcher's site, else the
     * page's default. A `site_id` equal to this arrived from context, not from the user picking it,
     * so it is not an active filter (POS2-1049). Note this deliberately excludes a site restored
     * from the shared store: that one *was* the user's choice on an earlier page. */
    scopeSiteId: switcherSiteId || defaultSiteId || null,
    timezone,
    hasActiveFilters,
    setFilter,
    setPreset,
    setSort,
    setCustomRange,
    setRange,
    applyFilters,
    clear,
  };
}

function parseLevel(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  return n >= 1 && n <= 4 ? n : null;
}

/** Tri-state confirmation param: "1" → true, "0" → false, absent/other → null (all). */
function parseTriBool(raw: string | null): boolean | null {
  if (raw === "1") return true;
  if (raw === "0") return false;
  return null;
}

function parseNum(raw: string | null, fallback: number, min: number, max: number): number {
  if (!raw) return fallback;
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function parsePageSize(raw: string | null): number {
  if (!raw) return DEFAULTS.page_size;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return DEFAULTS.page_size;
  // Match backend cap (200).
  return Math.min(200, Math.max(1, n));
}

// URL param names that differ from the filter key (kept short for brevity).
const PARAM_ALIAS: Partial<Record<keyof WasteFilters, string>> = {
  classification_level: "tier",
  is_confirmed: "confirmed",
};

// Fields dropped from the URL when they equal their default (keeps URLs clean).
const OMIT_AT_DEFAULT: readonly (keyof WasteFilters)[] = [
  "sort_by",
  "sort_dir",
  "page_size",
  "price_min",
  "price_max",
  "weight_min",
  "weight_max",
];

function writeParam<K extends keyof WasteFilters>(
  params: URLSearchParams,
  key: K,
  value: WasteFilters[K] | null,
): void {
  const paramKey = PARAM_ALIAS[key] ?? (key as string);
  // Toggles encode only the non-default state (so default-on toggles stay out
  // of the URL): omit at the default, else write "1"/"0".
  if (typeof value === "boolean") {
    if (value === DEFAULTS[key]) params.delete(paramKey);
    else params.set(paramKey, value ? "1" : "0");
    return;
  }
  if (value == null || value === "" || (key === "page" && value === 0)) {
    params.delete(paramKey);
    return;
  }
  if (OMIT_AT_DEFAULT.includes(key) && value === DEFAULTS[key]) {
    params.delete(paramKey);
    return;
  }
  params.set(paramKey, String(value));
}

// The shared filter subset persisted across waste pages (POS2-852): the popover
// draft fields + the date preset. Page/sort/status are deliberately excluded.
//
// Explore's own controls stay OUT of this list (POS2-941): `gran`, `measure`, `split` and `view`
// are view state, not filters — no other page has the concept, so persisting them would carry
// meaning to pages that cannot express it. Consequence, accepted: leaving Explore and returning
// loses the split and view, the same way page/sort are not carried. `split` is the one to watch —
// it is committed through `applyFilters`' `extraParams` so Apply stays one atomic write, which
// makes it look like a shared field without being one. Both halves are pinned by tests.
//
// `measure` is the arguable one: the dashboard's `lens` is the same £/kg choice under another
// name, and POS2-886 already carries it across a deep link. Unifying them would be a shared
// *preference*, not a filter, so it wants its own ticket rather than a line here.
const SHARED_DRAFT_KEYS: readonly (keyof WasteFilterDraft)[] = [
  "date_from",
  "date_to",
  "time_from",
  "time_to",
  "price_min",
  "price_max",
  "weight_min",
  "weight_max",
  "site_id",
  "kitchen_id",
  "waste_type",
  "device_serial",
  "food",
  "service_time",
  "classification_level",
  "is_confirmed",
];

// URL param names that signal an explicit filter is present. If any is set, the URL
// wins and we don't seed from the store (commentEvent/comment/page/sort are not here,
// so a bare deep-link still seeds).
//
// `preset` belongs here even though it no longer travels between pages (POS2-1052/1053): this
// list is "the URL is already expressing filter intent", not "what gets carried over". A link
// carrying `?preset=30d` must keep its window rather than have the store seed over it. The two
// sets are deliberately different — dropping `preset` here would break that.
const SHARED_PARAM_KEYS: readonly string[] = [
  "preset",
  ...SHARED_DRAFT_KEYS.map((k) => PARAM_ALIAS[k] ?? k),
];

/** What travels to the next page: exactly the popover draft, which is to say the filters the user
 * set. The date **preset** is deliberately not among them (POS2-1052/1053) — see
 * `writeSharedToParams` below and the note on `SharedWasteFilters`. */
function pickShared(f: WasteFilters): SharedWasteFilters {
  return toFilterDraft(f);
}

function writeSharedToParams(params: URLSearchParams, s: SharedWasteFilters): void {
  // The preset itself never travels (POS2-1052/1053) — only dates the user typed do, so the
  // destination reads them as a custom range. Either bound alone is enough: "from the 20th" is
  // as deliberate as a full range. With no dates carried, the page keeps its own default window.
  if (s.date_from || s.date_to) params.set("preset", "custom");
  else params.delete("preset");
  writeParam(params, "date_from", s.date_from);
  writeParam(params, "date_to", s.date_to);
  writeParam(params, "time_from", s.time_from);
  writeParam(params, "time_to", s.time_to);
  writeParam(params, "price_min", s.price_min);
  writeParam(params, "price_max", s.price_max);
  writeParam(params, "weight_min", s.weight_min);
  writeParam(params, "weight_max", s.weight_max);
  writeParam(params, "site_id", s.site_id);
  writeParam(params, "kitchen_id", s.kitchen_id);
  writeParam(params, "waste_type", s.waste_type);
  writeParam(params, "device_serial", s.device_serial);
  writeParam(params, "food", s.food);
  writeParam(params, "service_time", s.service_time);
  writeParam(params, "classification_level", s.classification_level);
  writeParam(params, "is_confirmed", s.is_confirmed);
}

/** The civil day a date picker stored for a bound. Both pickers write the picked day at the
 * VIEWER's local midnight, so the day is recovered in that same zone before being re-anchored
 * to the site's — the trick `useExploreState` already uses for Explore's custom range. */
function pickedDay(iso: string | null): string | null {
  return iso ? format(parseISO(iso), "yyyy-MM-dd") : null;
}

/**
 * The preset's window as inclusive civil days in the site's zone (POS2-1039). Pure calendar
 * string math — the zone enters only when the two ends are anchored to instants, so there is
 * no DST edge case here at all. A null bound is unbounded.
 *
 * The day-anchored presets close at the end of `today` (POS2-835): an open `to` reaches the
 * RPC as no `date_to`, read there as `+infinity` (`COALESCE(p_date_to, 'infinity')`), so a
 * future-dated event — a device with a skewed clock, an import with a bad date — would land
 * in "today" and "this week" and inflate the headline KPI and its WoW pill.
 */
export function computeDayRange(filters: WasteFilters, today: string): DayRange {
  if (filters.preset === "today") return { from: today, to: today };
  if (filters.preset === "7d") return { from: shiftDay(today, -7), to: null };
  if (filters.preset === "30d") return { from: shiftDay(today, -30), to: null };
  if (filters.preset === "90d") return { from: shiftDay(today, -90), to: null };
  if (filters.preset === "this_week") {
    // "This week so far" (POS2-865) — Monday to the end of today, NOT to the end of
    // the week. Closing at today keeps the WoW pill's same-weekday-slice comparison
    // honest (POS2-956) and is what makes the period bounded at all; a Sunday bound
    // would still admit future-dated rows for the rest of the week.
    return { from: weekStartDay(today), to: today };
  }
  if (filters.preset === "last_week") {
    const start = shiftDay(weekStartDay(today), -7);
    return { from: start, to: shiftDay(start, 6) };
  }
  if (filters.preset === "custom") {
    return { from: pickedDay(filters.date_from), to: pickedDay(filters.date_to) };
  }
  return { from: null, to: null };
}

export { DEFAULTS as WASTE_FILTER_DEFAULTS };
